import React, { useEffect, useState } from 'react';
import axios from 'axios';
import DoorlyProLayout, { RequirePartnerAuth } from '../../components/DoorlyProLayout';
import { useDoorlyProAuth } from '../../context/DoorlyProAuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const COMP_STATUS_STYLES = {
  pending: { bg: '#fefce8', color: '#ca8a04' },
  approved: { bg: '#acf44e', color: '#1a1a1a' },
  paid: { bg: '#eff6ff', color: '#2563eb' },
  voided: { bg: '#f3f4f6', color: '#6b7280' },
};

function CompBadge({ status }) {
  const s = COMP_STATUS_STYLES[status] || COMP_STATUS_STYLES.pending;
  return (
    <span style={{
      padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600,
      backgroundColor: s.bg, color: s.color,
    }}>
      {(status || 'Pending').replace(/\b\w/g, (c) => c.toUpperCase())}
    </span>
  );
}

export default function DoorlyProCompensation() {
  const { token } = useDoorlyProAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API_URL}/api/doorlypro/compensation`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setItems(res.data?.compensation || res.data || []))
      .catch(() => setError('Failed to load compensation records.'))
      .finally(() => setLoading(false));
  }, [token]);

  const totalPaid = items.filter((i) => i.status === 'paid').reduce((sum, i) => sum + Number(i.amount || 0), 0);
  const totalPending = items.filter((i) => i.status === 'pending' || i.status === 'approved').reduce((sum, i) => sum + Number(i.amount || 0), 0);

  return (
    <RequirePartnerAuth>
      <DoorlyProLayout>
        <div style={{ maxWidth: 800 }}>
          <div style={{ marginBottom: 24 }}>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: '#111827', margin: 0 }}>Compensation</h1>
            <p style={{ fontSize: 14, color: '#6b7280', marginTop: 4 }}>Track your earnings and payment history.</p>
          </div>

          {/* Summary */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
            <div style={{ backgroundColor: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', padding: '20px 24px' }}>
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 6, fontWeight: 500 }}>TOTAL PAID</div>
              <div style={{ fontSize: 26, fontWeight: 800, color: '#acf44e' }}>
                ${totalPaid.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </div>
            </div>
            <div style={{ backgroundColor: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', padding: '20px 24px' }}>
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 6, fontWeight: 500 }}>PENDING</div>
              <div style={{ fontSize: 26, fontWeight: 800, color: '#ca8a04' }}>
                ${totalPending.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </div>
            </div>
          </div>

          {error && (
            <div style={{ padding: '12px 16px', borderRadius: 8, backgroundColor: '#fff0f3', border: '1px solid #ffb3bf', color: '#ff082f', fontSize: 13, marginBottom: 16 }}>
              {error}
            </div>
          )}

          <div style={{ backgroundColor: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
            {loading ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>Loading...</div>
            ) : items.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>
                No compensation records yet. Earnings will appear here after referrals close.
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                      {['Description', 'Amount', 'Status', 'Buyer', 'Date'].map((col) => (
                        <th key={col} style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, color: '#6b7280', fontSize: 12 }}>
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, idx) => (
                      <tr key={item.id || idx} style={{ borderBottom: idx < items.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                        <td style={{ padding: '12px 16px', color: '#111827' }}>{item.description || '—'}</td>
                        <td style={{ padding: '12px 16px', fontWeight: 700, color: '#111827' }}>
                          ${Number(item.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <CompBadge status={item.status} />
                        </td>
                        <td style={{ padding: '12px 16px', color: '#6b7280' }}>
                          {item.referral_id ? `#${String(item.referral_id).padStart(4, '0')}` : '—'}
                        </td>
                        <td style={{ padding: '12px 16px', color: '#6b7280', whiteSpace: 'nowrap' }}>
                          {item.created_at ? new Date(item.created_at).toLocaleDateString() : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </DoorlyProLayout>
    </RequirePartnerAuth>
  );
}
